/**
 * Evolve Analytics — design tokens, two modes ("Evolve Theme", soft style).
 *
 * Tokens are named by ROLE (`surface`, not `white`): a role can flip between
 * modes, a colour name cannot. Values come from the design sheet; the comment
 * on each names its role there (`bg/page`, `text/link`, …).
 *
 * The lime accent is a FILL only: dark text sits on it, and it never colours
 * text, icons or thin lines on a light surface (1.2:1 on white). Those take
 * `link` — the interactive blue — instead.
 */

import { DEFAULT_CHART_THEME, chartTheme } from './chartThemes';

const LIGHT = {
  // Surfaces
  canvas:    '#F6F6F2',  // bg/page
  surface:   '#FFFFFF',  // bg/surface — cards, inputs, menus, dialogs
  raised:    '#F1F1EC',  // bg/surface-alt — nested blocks, zebra, hover
  highlight: '#F5F5E8',  // bg/highlight — selected row or card
  skeleton:  '#ECEBE4',  // bg/skeleton
  overlay:   'rgba(23,23,26,0.45)',  // bg/overlay, on the sheet's own ink

  // Sidebar — dark in both modes
  chrome:         '#17171A',  // bg/sidebar
  chromeAlt:      '#1F1F23',  // bg/sidebar-alt — nested block, hover
  onChrome:       '#FFFFFF',  // text/on-dark
  onChromeMuted:  'rgba(255,255,255,0.62)',
  onChromeAccent: '#D4FF00',  // text/on-inverse-accent

  // Action
  accent:      '#D4FF00',  // action/primary — primary button, active item, marker
  accentHover: '#C4EF00',  // action/primary-hover
  onAccent:    '#17171A',  // text/on-accent

  // Text
  ink:      '#17171A',    // text/primary
  inkMuted: '#5C5C55',    // text/secondary — captions, hints
  inkDim:   '#17171A66',  // text/placeholder — placeholder and disabled only
  link:     '#2F43C4',    // text/link — links, info text, the MUI primary

  // Status: text/icon colour, and the wash it sits on
  positive:   '#0F6B4B',  positiveBg: '#E6F6EF',  // text/positive, bg/success
  negative:   '#C42B2B',  negativeBg: '#FDECEC',  // text/negative, bg/danger
  warning:    '#8A5A00',  warningBg:  '#FFF3DE',  // text/warning,  bg/warning
  info:       '#2F43C4',  infoBg:     '#EAEEFC',  // icon/info,     bg/info

  iconMuted: '#8A8A81',  // icon/secondary — 3:1, lighter than text

  // Status as a FILL (marks, cells, bars): the status text colours above are
  // dark for contrast on white and read as brown/olive when they fill a shape.
  positiveFill: '#3BA57A',
  warningFill:  '#E2A93B',
  negativeFill: '#D9534F',

  // Lines
  border:       '#EBEAE4',  // border/subtle — dividers, cards at rest
  borderField:  '#DEDCD4',  // border/default — input outline
  borderStrong: '#17171A',  // border/strong — selected card
  focus:        '#2F43C4',  // border/focus — keyboard focus ring

  // Charts: data/series-1…4
  series: ['#4361EE', '#E86129', '#7C5CFF', '#00B894'],

  shadowColor: 'rgba(23,23,26,0.05)',  // shadow/soft
};

const DARK = {
  canvas:    '#0F0F11',
  surface:   '#17171A',
  raised:    '#1D1D21',
  highlight: '#2A2D14',
  skeleton:  '#26261F',
  overlay:   'rgba(0,0,0,0.65)',

  chrome:         '#121214',
  chromeAlt:      '#1A1A1E',
  onChrome:       '#F4F4EF',
  onChromeMuted:  'rgba(244,244,239,0.62)',
  onChromeAccent: '#BCE202',

  accent:      '#BCE202',
  accentHover: '#C3E51B',
  onAccent:    '#17171A',

  ink:      '#F4F4EF',
  inkMuted: '#F4F4EFB8',
  inkDim:   '#F4F4EF66',
  link:     '#A2B2FF',

  positive:   '#3DC48C',  positiveBg: '#10281F',
  negative:   '#FF8B8B',  negativeBg: '#2B1616',
  warning:    '#F3BD61',  warningBg:  '#2C2113',
  info:       '#A2B2FF',  infoBg:     '#191D38',

  iconMuted: '#F4F4EF80',

  positiveFill: '#3DC48C',
  warningFill:  '#F3BD61',
  negativeFill: '#FF8B8B',

  border:       '#FFFFFF1A',
  borderField:  '#FFFFFF29',
  borderStrong: '#F4F4EF',
  focus:        '#BCE202',

  series: ['#7F94FF', '#FF8D61', '#A78BFF', '#3AD0AE'],

  shadowColor: 'rgba(0,0,0,0.40)',
};

/**
 * MUI's grey ramp, warm to match the sheet, and its exact mirror: MUI does NOT
 * invert `palette.grey` for dark mode, so mirroring keeps `grey[N]` meaning
 * "N steps away from the background". Rungs are sheet colours where the sheet
 * has one; 400 and 700 are interpolated between their neighbours.
 */
const GREY_LIGHT = {
  50:  '#F6F6F2', 100: '#F1F1EC', 200: '#EBEAE4', 300: '#DEDCD4', 400: '#B4B3AB',
  500: '#8A8A81', 600: '#5C5C55', 700: '#3D3D3C', 800: '#1F1F23', 900: '#17171A',
};
const GREY_DARK = {
  50:  '#17171A', 100: '#1F1F23', 200: '#3D3D3C', 300: '#5C5C55', 400: '#8A8A81',
  500: '#B4B3AB', 600: '#DEDCD4', 700: '#EBEAE4', 800: '#F1F1EC', 900: '#F6F6F2',
};

/** The soft shadow ladder: one colour (shadow/soft), four distances. */
export const shadowScale = (mode) => {
  const c = (mode === 'dark' ? DARK : LIGHT).shadowColor;
  return [
    'none',
    `0 1px 2px ${c}, 0 4px 12px ${c}`,
    `0 2px 4px ${c}, 0 8px 24px ${c}`,
    `0 4px 8px ${c}, 0 12px 32px ${c}`,
    `0 8px 16px ${c}, 0 24px 48px ${c}`,
  ];
};

/**
 * Every token for one mode, flat. Read it off `theme.palette.nb`. `series` also
 * colours the interface (MUI `secondary`, BI channels), so charts read `chart`.
 */
export const tokens = (mode = 'light', chartThemeName = DEFAULT_CHART_THEME) => {
  const colours = mode === 'dark' ? DARK : LIGHT;
  return {
    mode,
    ...colours,
    chart: chartTheme(chartThemeName, mode, colours),
    grey: mode === 'dark' ? GREY_DARK : GREY_LIGHT,
  };
};

export { GREY_LIGHT, GREY_DARK };
